/* eslint-disable no-console */
import type { Context as KoaContext, Next as KoaNext } from 'koa'
import { createRateLimiter as createCore } from '../../core/RateLimiter'
import type { RateShieldContext } from '../../context'
import type {
  RateLimitInfo,
  RateLimitOptions,
  RateLimitStats
} from '../../types'
import { extractIp } from './helpers'

// ── Koa-specific instance type ─────────────────────────────────────────────────

/**
 * A rate-limiter instance that works as Koa middleware:
 *
 * ```ts
 * const limiter = createRateLimiter({ maxRequests: 100 })
 * app.use(limiter)
 * ```
 */
export interface KoaRateLimiterInstance {
  (ctx: KoaContext, next: KoaNext): Promise<void>
  destroy(): void | Promise<void>
  reset(key: string): void | Promise<void>
  resetAll(): void | Promise<void>
  getInfo(key: string): Promise<RateLimitInfo | null>
  getStats(): Promise<RateLimitStats>
}

// ── Koa factory ────────────────────────────────────────────────────────────────

/**
 * Create a rate-limiter instance that works as Koa middleware.
 *
 * @example
 * ```ts
 * import Koa from 'koa'
 * import { createRateLimiter } from 'rate-shield/koa'
 *
 * const app    = new Koa({ proxy: true })
 * const limiter = createRateLimiter({ maxRequests: 100, windowMs: 60_000 })
 *
 * app.use(limiter)
 * ```
 */
export const createRateLimiter = (
  options: RateLimitOptions<KoaContext> = {}
): KoaRateLimiterInstance => {
  const core = createCore<KoaContext>({
    keyGenerator: extractIp,
    ...options
  })

  const middleware = async (ctx: KoaContext, next: KoaNext): Promise<void> => {
    let passed = false

    const rateCtx: RateShieldContext<KoaContext> = {
      req: ctx,
      setHeader: (name, value) => {
        if (!ctx.headerSent) ctx.set(name, String(value))
      },
      isResponseSent: () => ctx.headerSent,
      block: (_statusCode, body) => {
        if (!ctx.headerSent) {
          ctx.status = 429
          ctx.body = body
        }
      },
      pass: () => {
        passed = true
      }
    }

    await core.handle(rateCtx)
    if (passed) await next()
  }

  return Object.assign(middleware, {
    destroy: () => core.destroy(),
    reset: (key: string) => core.reset(key),
    resetAll: () => core.resetAll(),
    getInfo: (key: string) => core.getInfo(key),
    getStats: () => core.getStats()
  }) as KoaRateLimiterInstance
}
